import type { ParentClassUpdateSemesterGroupDTO } from "@/orval/model";
import { PortalPageHeader } from "@/components/portal-page-header";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useParentClassUpdatesGetClasses } from "@/orval/parent-class-updates";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parse } from "date-fns";
import { ArrowRightIcon, BookOpenIcon, ClockIcon, UserIcon } from "lucide-react";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_app/organizations/$organizationId/parent/classes")({
  component: RouteComponent,
});

const timeParseBaseDate = new Date(0);

function formatClassTime(time: string) {
  return format(parse(time, "HH:mm:ss", timeParseBaseDate), "h:mm a");
}

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const { data, isLoading } = useParentClassUpdatesGetClasses(organizationId);
  const [selectedSemesterId, setSelectedSemesterId] = useState<string>();

  const effectiveSemesterId = selectedSemesterId ?? data?.defaultSemesterId?.toString();
  const selectedSemester = useMemo(
    () => data?.semesters.find((semester) => semester.semesterId.toString() === effectiveSemesterId),
    [data?.semesters, effectiveSemesterId],
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-4"
        title="Classes"
        subtitle="View enrolled classes by student"
        actions={
          data && data.semesters.length > 0 ? (
            <Select value={effectiveSemesterId} onValueChange={setSelectedSemesterId}>
              <SelectTrigger className="w-full sm:w-72">
                <SelectValue placeholder="Select semester" />
              </SelectTrigger>
              <SelectContent>
                {data.semesters.map((semester) => (
                  <SelectItem key={semester.semesterId} value={semester.semesterId.toString()}>
                    {semester.semesterName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null
        }
      />

      <SemesterClasses
        organizationId={organizationId}
        isLoading={isLoading}
        hasSemesters={!!data?.semesters.length}
        selectedSemester={selectedSemester}
      />
    </div>
  );
}

function SemesterClasses({
  organizationId,
  isLoading,
  hasSemesters,
  selectedSemester,
}: {
  organizationId: number;
  isLoading: boolean;
  hasSemesters: boolean;
  selectedSemester?: ParentClassUpdateSemesterGroupDTO;
}) {
  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }
  if (!hasSemesters) {
    return <div className="rounded-md border p-6 text-sm text-muted-foreground">No enrolled classes yet.</div>;
  }
  return (
    <>
      {" "}
      {selectedSemester ? (
        <div className="space-y-6">
          {selectedSemester.studentGroups.map((group) => (
            <section key={group.studentId} className="space-y-3">
              <h2 className="text-lg font-semibold">{group.studentName}</h2>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {group.classes.map((classItem) => (
                  <Link
                    key={`${group.studentId}-${classItem.id}`}
                    className="group block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    from={Route.fullPath}
                    to="/organizations/$organizationId/parent/classes/$classId"
                    params={{ organizationId, classId: classItem.id }}
                  >
                    <Card className="h-full transition-all hover:border-primary/50 hover:shadow-md">
                      <CardContent className="flex gap-3 p-4">
                        {classItem.imageUrl ? (
                          <img src={classItem.imageUrl} alt="" className="size-16 rounded-md object-cover" />
                        ) : (
                          <div className="flex size-16 items-center justify-center rounded-md bg-muted">
                            <BookOpenIcon className="size-6 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-primary underline-offset-4 group-hover:underline">
                            {classItem.name}
                          </div>
                          <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                            <UserIcon className="size-3.5" />
                            <span className="truncate">{classItem.teacherName || "Teacher TBD"}</span>
                          </div>
                          <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                            <ClockIcon className="size-3.5" />
                            <span>
                              {classItem.classDayName}, {formatClassTime(classItem.startTime)}-
                              {formatClassTime(classItem.endTime)}
                            </span>
                          </div>
                          <Badge variant="outline" className="mt-2">
                            {classItem.semesterName}
                          </Badge>
                          <div className="mt-3 flex items-center text-sm font-medium text-primary">
                            View class details
                            <ArrowRightIcon className="ml-1 size-4 transition-transform group-hover:translate-x-0.5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : null}
    </>
  );
}
